import { Component, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import {
  SidebarComponent,
  HeaderComponent,
  ErrorLoadingSystemComponent,
  SupportModalComponent,
  ApplicationService,
  SidebarService,
  UserService,
} from '@vtnz/ui';
import type { NavItem } from '@vtnz/ui';

import { CustomerAccount, CustomerAccountLookup, UserTypeEnum } from '@vtnz/api';

@Component({
  selector: 'app-base-layout',
  imports: [
    CommonModule,
    RouterModule,
    SidebarComponent,
    HeaderComponent,
    ErrorLoadingSystemComponent,
    SupportModalComponent,
  ],
  templateUrl: './base-layout.component.html',
})
export class BaseLayoutComponent {
  public sidebarService = inject(SidebarService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly applicationService = inject(ApplicationService);
  private readonly userService = inject(UserService);

  applicationTitle = signal<string | undefined>(undefined);

  consumerMenuItems = new Set([
    'Home',
    'Manage Vehicles',
    'Book inspection',
    'Digital Vouchers',
    'MBI Insurance',
    'My Profile',
    'Logout',
  ]);

  commercialMenuItems = new Set([
    'Home',
    'Manage Vehicles',
    'Billing',
    'Book inspection',
    'Digital Vouchers',
    'MBI Insurance',
    'Reports',
    'Logout',
  ]);

  // Main nav items
  private readonly allNavItems = signal<NavItem[]>([
    {
      icon: `<svg class="flex-shrink-0 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
          </svg>`,
      name: 'Home',
      path: '/home',
      menuGroup: 'top',
    },
    {
      icon: `<svg class="flex-shrink-0 w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
          </svg>`,
      name: 'Manage Vehicles',
      path: '/manage-vehicles',
      menuGroup: 'top',
    },
    {
      icon: `<svg class="flex-shrink-0 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>`,
      name: 'Billing',
      path: '/billing',
      menuGroup: 'top',
    },
    {
      icon: `<svg class="flex-shrink-0 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>`,
      name: 'Book inspection',
      externalLink: 'https://vtnz-booking.appointy.com/vtnz',
      menuGroup: 'top',
    },
    {
      icon: `<svg class="flex-shrink-0 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0H7m5 0v-5a2 2 0 00-2-2H8a2 2 0 00-2 2v5m5 0V9a2 2 0 012-2h2a2 2 0 012 2v12"></path>
          </svg>`,
      name: 'Book inspection Dekra',
      path: '/book-inspection',
      menuGroup: 'top',
    },
    {
      icon: `<svg class="flex-shrink-0 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
          </svg>`,
      name: 'Digital Vouchers',
      externalLink: 'https://vtnz.co.nz/digital-wof-voucher',
      menuGroup: 'top',
    },
    {
      icon: `<svg class="flex-shrink-0 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
          </svg>`,
      name: 'MBI Insurance',
      externalLink: 'https://vtnz.co.nz/mbi?utm_source=MyVTNZ',
      menuGroup: 'top',
    },
    {
      icon: `<svg class="flex-shrink-0 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>`,
      name: 'Reports',
      path: '/reports',
      menuGroup: 'top',
    },
    {
      icon: `<svg class="flex-shrink-0 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
          </svg>`,
      name: 'My Profile',
      path: '/my-profile',
      menuGroup: 'bottom',
    },
    {
      icon: `<svg class="flex-shrink-0 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
          </svg>`,
      name: 'Logout',
      path: '/logout',
      menuGroup: 'bottom',
    },
  ]);

  isLoadingUserContext = signal<boolean>(true);
  userContextError = signal<string | null>(null);
  isSupportModalOpen = signal<boolean>(false);

  currentUserContext = computed(() => {
    return this.userService.userContext() ?? undefined;
  });

  userMenuItems = computed(() => {
    if (!this.userService.userContext()) return [];

    //filter items based on the feature toggle
    const navItems = this.applicationService.isBookingEnabled()
      ? this.allNavItems()
      : this.allNavItems().filter((item) => item.path !== '/book-inspection');

    //filter items based on user type
    const userType = this.currentUserContext()?.userType;

    if (userType === UserTypeEnum.Consumer) {
      return navItems.filter((item) => this.consumerMenuItems.has(item.name));
    }

    if (userType === UserTypeEnum.Commercial) {
      const commercialItems = navItems.filter((item) => this.commercialMenuItems.has(item.name));
      if (!this.applicationService.isCommercialManageVehiclesEnabled()) {
        return commercialItems.filter((item) => item.path !== '/manage-vehicles');
      }
      return commercialItems;
    }

    return navItems;
  });

  topNavItems = computed(() => this.userMenuItems().filter((item) => item.menuGroup === 'top'));

  bottomNavItems = computed(() =>
    this.userMenuItems().filter((item) => item.menuGroup === 'bottom')
  );

  selectedCustomerAccount = computed((): CustomerAccountLookup | undefined => {
    const account = this.userService.selectedCustomerAccount();
    if (!account) return undefined;

    return account;
  });

  accounts = computed((): CustomerAccountLookup[] => {
    const accountCustomers =
      (this.currentUserContext()?.accountCustomers.filter(
        // Map to ensure all required properties are present and cast to AccountCustomer
        (account): account is Required<CustomerAccount> =>
          !!account.accountCustomerCode && !!account.name
      ) as CustomerAccountLookup[]) ?? [];

    return accountCustomers;
  });

  // Automatically select the first account if there's only one account and none is selected
  constructor() {
    effect(() => {
      const accountCustomers = this.accounts();
      const selectedAccount = this.selectedCustomerAccount();

      if (accountCustomers.length === 1 && !selectedAccount) {
        this.onAccountSelected(accountCustomers[0]);
      }
    });

    // Track loading state of userContext
    effect(() => {
      const userContext = this.currentUserContext();
      if (userContext !== undefined) {
        this.isLoadingUserContext.set(false);
        this.userContextError.set(null);
      }
    });

    // Set a timeout to detect if userContext fails to load
    setTimeout(() => {
      if (this.isLoadingUserContext()) {
        this.isLoadingUserContext.set(false);
        this.userContextError.set('Failed to load system data. Please try refreshing the page.');
      }
    }, 10000); // 10 second timeout
  }

  // Get applicationTitle from route data if not provided as input
  readonly resolvedApplicationTitle = computed(() => {
    const inputTitle = this.applicationTitle();
    if (inputTitle) {
      return inputTitle;
    }
    return this.activatedRoute.snapshot.data['applicationTitle'] || 'VTNZ';
  });

  get containerClasses() {
    return [
      'flex-1',
      'transition-all',
      'duration-300',
      'ease-in-out',
      this.sidebarService.isExpanded() || this.sidebarService.isHovered()
        ? 'xl:ml-[230px]'
        : 'xl:ml-[52px]',
      this.sidebarService.isMobile() ? 'ml-0' : '',
    ];
  }

  onAccountSelected(account: CustomerAccountLookup | null) {
    this.userService.setSelectedCustomerAccount(account);
  }

  retryLoadUserContext() {
    this.isLoadingUserContext.set(true);
    this.userContextError.set(null);

    // Force reload by clearing and re-fetching
    globalThis.location.reload();
  }

  toggleSupportModal() {
    this.isSupportModalOpen.set(!this.isSupportModalOpen());
  }

  closeSupportModal() {
    this.isSupportModalOpen.set(false);
  }
}
